import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Divider,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  FormHelperText,
} from "@mui/material";
import bannerImage from "../../assets/banner.jpeg";
import { Formik, Form, useField } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { showAppMessageToast } from "../../utils/toastUtils";
import {
  resendOtp,
  verifyOtpAfterRegister,
  verifyOtpForLogin,
  setCredentials,
} from "../../features/auth/authSlice";
import {
  fetchUserProfile,
  switchRole,
  fetchUserAddresses,
} from "../../features/user/userSlice";
import { useDispatch } from "react-redux";

// This file is a self-contained React application that recreates the Request OTP page
// using Material-UI components. All external dependencies are mocked to
// make this a runnable, single-file example.

// MOCKING EXTERNAL DEPENDENCIES FOR A STANDALONE EXAMPLE
// const useNavigate = () => (path) => console.log(`Navigating to: ${path}`);
// const useLocation = () => ({
//   state: { email: "user@example.com", source: "register" },
//   search: "",
// });
// // const useDispatch = () => () => ({ unwrap: () => Promise.resolve({ role: "buyer" }) });
// // const showAppMessageToast = ({ type, title, message }) => console.log(`${type}: ${title}\n${message}`);
// // const bannerImage = "https://placehold.co/1080x1920/1A237E/fff?text=Verify+OTP+Banner";

// Custom theme to match the colors from the login and register pages
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#ff9800",
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27",
            opacity: 1,
          },
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
  },
});

// A custom OTP input component
const OtpInput = ({ name, length, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const [otpValues, setOtpValues] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Sync Formik value with internal state
    helpers.setValue(otpValues.join(""));
  }, [otpValues, helpers]);

  const handleChange = (index, event) => {
    const { value } = event.target;
    if (/[^0-9]/.test(value) && value !== "") return; // Allow empty string for backspace

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1); // Only take the last character

    setOtpValues(newOtpValues);

    // Focus next input field automatically
    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    // Move to previous input on backspace if current is empty
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // ✅ Allow full OTP paste (handles "123456")
  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasteData)) return;

    const digits = pasteData.slice(0, length).split("");
    const newOtpValues = new Array(length).fill("");
    for (let i = 0; i < digits.length; i++) {
      newOtpValues[i] = digits[i];
    }

    setOtpValues(newOtpValues);

    // Focus the next empty input after paste
    const nextEmptyIndex = digits.length < length ? digits.length : length - 1;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  const errorText = meta.error && meta.touched ? meta.error : "";

  return (
    <Box
      sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2, mb: 1 }}
    >
      {otpValues.map((value, index) => (
        <TextField
          key={index}
          value={value}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputRef={(el) => (inputRefs.current[index] = el)}
          variant="outlined"
          sx={{
            width: 48,
            "& .MuiOutlinedInput-root": {
              textAlign: "center",
              "& input": {
                textAlign: "center",
              },
              // CHANGED: Add conditional styling for highlighted field
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: customTheme.palette.scraptradeYellow.main,
              },
              "&.Mui-focused": {
                backgroundColor: "#fff",
              },
            },
            // CHANGED: Apply highlight style if the field has a value
            ...(value && {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: customTheme.palette.scraptradeYellow.main,
                borderWidth: "2px",
              },
            }),
          }}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center", fontWeight: 600, fontSize: "18px" },
          }}
          error={!!errorText}
        />
      ))}
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Box>
  );
};

const RequestOtp = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;
  const source = location.state?.source;
  const role =
    location.state?.role || localStorage.getItem("user_role") || "buyer";
  console.log("selected role:", role);

  const [timeLeft, setTimeLeft] = useState(300);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [isTimerVisible, setIsTimerVisible] = useState(true);

  useEffect(() => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setResendEnabled(true);
            setIsTimerVisible(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (role) {
      localStorage.setItem("user_role", role);
    }
  }, [role]);

  const handleVerifyOtp = async (values) => {
    const finalOtp = values.otp;

    if (finalOtp.length !== 6) return;

    if (!email || !source) {
      setError("Missing information. Please try again.");
      showAppMessageToast({
        type: "error",
        title: "OTP Verification Failed",
        message: "Missing information. Please try again.",
      });
      return;
    }

    try {
      setError("");
      const payload = { email, otp: finalOtp, role };
      console.log("payload", payload);
      const result =
        source === "login"
          ? await dispatch(verifyOtpForLogin(payload)).unwrap()
          : await dispatch(verifyOtpAfterRegister(payload)).unwrap();

      showAppMessageToast({
        type: "success",
        title: "OTP Verified",
        message: "OTP verification successful.",
      });

      const verifiedRole = result.role || role;
      console.log("STEP 1: OTP Result Role:", result.role);
      console.log("STEP 2: Local Role Variable:", role);
      console.log("STEP 3: Final Verified Role used for nav:", verifiedRole);

      const profileRes = await dispatch(fetchUserProfile()).unwrap();
      console.log("STEP 4: Raw Profile Response:", profileRes);

      let user =
        profileRes?.data ||
        profileRes?.user ||
        (profileRes?.status === true ? profileRes.data : profileRes);

      // 🚀 FETCH ADDRESSES FOR SELLERS
      let addressCount = 0;
      if (verifiedRole === "seller") {
        try {
          const addressRes = await dispatch(fetchUserAddresses()).unwrap();
          const addressList = addressRes?.data || addressRes;
          addressCount = Array.isArray(addressList) ? addressList.length : 0;
          user = { ...user, addresses_count: addressCount };
        } catch (addrErr) {
          console.error("Error fetching addresses in RequestOtp:", addrErr);
        }
      }

      // Sync state with the updated user (including addresses_count)
      dispatch(
        setCredentials({
          token: result.token || result.access_token,
          user: user,
          role: verifiedRole,
        }),
      );

      console.log("STEP 5: Extracted User Data:", user);
      console.log("STEP 5a: Profile Role from API:", user?.role);

      if (!user) {
        console.error("CRITICAL: User profile missing after verification");
      }

      // Check email + phone verified for BUYER (matching KycBanner pattern)
      const isEmailVerified =
        user?.email_verified_at !== null &&
        user?.email_verified_at !== undefined;
      const isPhoneVerified =
        user?.phone_verified_at !== null &&
        user?.phone_verified_at !== undefined;
      const isContactVerified = isEmailVerified && isPhoneVerified;

      // // OLD: kyc_verified check (commented out)
      // const kycStatus = user?.kyc_verified;
      // const isKycVerified =
      //   kycStatus === 1 || kycStatus === "1" || kycStatus === true;
      // console.log(
      //   "STEP 6: KYC Verified Status:",
      //   isKycVerified,
      //   "(Raw value:",
      //   kycStatus,
      //   ")",
      // );

      console.log("STEP 6: Contact Verification:", {
        isContactVerified,
        isEmailVerified,
        isPhoneVerified,
      });

      // CRITICAL: If the backend profile role doesn't match our intended role,
      // we might need an explicit switch to activate the session.
      if (user?.role && user.role !== verifiedRole) {
        console.warn(
          `STEP 6a: Role Mismatch! Profile is ${user.role}, but Navigating as ${verifiedRole}. Attempting explicit switch...`,
        );
        try {
          // ensure switchRole and verifyRoleOtp are imported from userSlice
          await dispatch(switchRole({ role: verifiedRole })).unwrap();
          console.log("STEP 6b: Explicit switchRole successful");
        } catch (switchErr) {
          console.error("STEP 6b: Explicit switchRole failed:", switchErr);
        }
      }

      if (source === "login") {
        console.log("STEP 7: Flow identified as LOGIN");
        if (verifiedRole === "buyer") {
          if (!isContactVerified) {
            console.log(
              "STEP 8: Navigating to Market (email/phone not verified) (Login flow)",
            );
            navigate("/market");
          } else {
            console.log("STEP 8: Navigating to Buyer Dashboard (Login flow)");
            navigate("/dashboard");
          }
        } else {
          // Seller flow - now uses email + phone verified (matching getSellerAccess)
          // KYC/bank verification is enforced at bid submission via popup
          if (isContactVerified) {
            console.log(
              "STEP 8: Navigating to Seller Dashboard (Login flow - contact verified)",
            );
            navigate("/seller/dashboard");
          } else {
            console.log(
              "STEP 8: Navigating to Seller Verification List (Login flow - contact not verified)",
            );
            navigate("/seller/verification-list");
          }
          // // OLD: kyc_verified seller redirect (commented out)
          // const kycStatus = user?.kyc_verified;
          // const isKycVerified = kycStatus === 1 || kycStatus === "1" || kycStatus === true;
          // if (!isKycVerified) {
          //   navigate("/seller/upload-kyc", { state: { email } });
          // } else if (user?.certification_verified === 0 || user?.certification_verified === "0") {
          //   navigate("/seller/certificate");
          // } else {
          //   navigate("/seller/dashboard");
          // }
        }
      } else {
        console.log("STEP 7: Flow identified as REGISTER");
        if (verifiedRole === "buyer") {
          if (isContactVerified) {
            console.log(
              "STEP 8: Navigating to Buyer Dashboard (Register flow - already verified)",
            );
            navigate("/dashboard");
          } else {
            console.log(
              "STEP 8: Navigating to Market (email/phone not verified) (Register flow)",
            );
            navigate("/market");
          }
        } else {
          // Seller flow - now uses email + phone verified (matching getSellerAccess)
          // KYC/bank verification is enforced at bid submission via popup
          if (isContactVerified) {
            console.log(
              "STEP 8: Navigating to Seller Dashboard (Register flow - contact verified)",
            );
            navigate("/seller/dashboard");
          } else {
            console.log(
              "STEP 8: Navigating to Seller Verification List (Register flow - contact not verified)",
            );
            navigate("/seller/verification-list");
          }
          // // OLD: kyc_verified seller redirect (commented out)
          // const kycStatus = user?.kyc_verified;
          // const isKycVerified = kycStatus === 1 || kycStatus === "1" || kycStatus === true;
          // if (isKycVerified) {
          //   if (user?.certification_verified === 0 || user?.certification_verified === "0") {
          //     navigate("/seller/certificate");
          //   } else {
          //     navigate("/seller/dashboard");
          //   }
          // } else {
          //   navigate("/seller/upload-kyc", { state: { email } });
          // }
        }
      }
    } catch (err) {
      console.error("OTP verification failed:", err);

      // ✅ err is a plain string coming from Redux thunk rejection
      const errorMessage =
        typeof err === "string" ? err : err?.message || "Failed to verify OTP";

      const is403 =
        err?.status === 403 ||
        err?.statusCode === 403 ||
        err?.response?.status === 403 ||
        errorMessage.includes("403") ||
        errorMessage.toLowerCase().includes("forbidden") ||
        errorMessage.toLowerCase().includes("verify your email"); // ✅ matches your exact case

      if (is403) {
        showAppMessageToast({
          type: "error",
          title: "Verification Required",
          message: errorMessage,
        });
        navigate("/login");
        return;
      }

      setError(errorMessage);
      showAppMessageToast({
        type: "error",
        title: "OTP Verification Failed",
        message: errorMessage,
      });
    }
  };

  const handleResendOtp = async () => {
    try {
      if (!email) {
        setError("Email not found. Please go back and try again.");
        showAppMessageToast({
          type: "error",
          title: "Resend OTP Failed",
          message: "Email not found. Please try again.",
        });
        return;
      }

      await dispatch(resendOtp({ email })).unwrap();
      setIsTimerVisible(true);
      setTimeLeft(300);
      setResendEnabled(false);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setResendEnabled(true);
            setIsTimerVisible(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      showAppMessageToast({
        type: "success",
        title: "OTP Resent",
        message: "A new OTP has been sent to your registered phone number.",
      });
    } catch (err) {
      console.error("Failed to resend OTP:", err);
      setError(err?.message || "Failed to resend OTP");
      showAppMessageToast({
        type: "error",
        title: "Resend OTP Failed",
        message: err?.message || "Failed to resend OTP. Please try again.",
      });
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - hidden on mobile */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${bannerImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />
          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              sx={{ my: 1, fontSize: "2.5rem" }}
            >
              Scrap Trade
            </Typography>
            <Typography variant="body1" sx={{ color: "#ddd" }}>
              Secure your account by verifying your email.
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            Verify OTP
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              mb: 3,
              color: customTheme.palette.text.secondary,
            }}
          >
            A 6-digit OTP has been sent to your registered mobile number: <br />{" "}
            **{email}**
          </Typography>

          <Formik initialValues={{ otp: "" }} onSubmit={handleVerifyOtp}>
            {({ values, isSubmitting }) => (
              <Form noValidate>
                <OtpInput name="otp" length={6} />

                {error && (
                  <FormHelperText error sx={{ textAlign: "center", mt: 1 }}>
                    {error}
                  </FormHelperText>
                )}

                {isTimerVisible && (
                  <Box sx={{ textAlign: "center", my: 2 }}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ color: customTheme.palette.text.secondary }}
                    >
                      OTP expires in {formatTime(timeLeft)}
                    </Typography>
                  </Box>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    backgroundColor: customTheme.palette.scraptradeYellow.main,
                    color: "#2D3A27",
                    "&:hover": {
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.dark,
                    },
                  }}
                  disabled={values.otp.length !== 6 || isSubmitting}
                >
                  Verify OTP
                </Button>

                <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleResendOtp}
                  sx={{
                    fontWeight: 600,
                    color: customTheme.palette.scraptradeYellow.main,
                    borderColor: customTheme.palette.scraptradeYellow.main,
                    "&:hover": {
                      borderColor: customTheme.palette.scraptradeYellow.dark,
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.main + "1a",
                    },
                  }}
                  disabled={isSubmitting || !resendEnabled}
                >
                  Resend OTP
                </Button>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    mt: 2,
                    mb: 3,
                    color: customTheme.palette.text.secondary,
                  }}
                >
                  A separate verification link has been sent to your email to
                  activate your account.
                </Typography>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <RequestOtp />
    </ThemeProvider>
  );
}
